import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Crown, User, Plus, Share2, MessageCircle, Copy, Check, Zap, Trophy, Settings, Sparkles } from 'lucide-react';
import { getMisl, subscribeMisl, type StoredMisl } from './mislStore';
const CornerOrnaments = () => <>
    <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-1971" />
    <span className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-1972" />
    <span className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-1973" />
    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-1974" />
  </>;
export const FoundingMislStatus = ({
  onManage
}: {
  onManage: () => void;
}) => {
  const [misl, setMislState] = useState<StoredMisl | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    setMislState(getMisl());
    const unsub = subscribeMisl(() => setMislState(getMisl()));
    return unsub;
  }, []);
  if (!misl) return null;
  const memberCount = misl.members.length;
  const progress = Math.min(100, Math.round(memberCount / misl.target * 100));
  const isActive = memberCount >= misl.target;
  const inviteLink = `lahoretreasury.com/misl/${misl.name.toLowerCase().replace(/\s+/g, '-')}`;
  const remaining = Math.max(0, misl.target - memberCount);
  const handleCopy = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(`https://${inviteLink}`).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const handleWhatsApp = () => {
    const msg = encodeURIComponent(`🛡️ Join my Misl on Lahore Treasury — ${misl.name}.\nWe need ${remaining} more to activate our house in the Court.\n\n${inviteLink}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };
  const nextSteps = isActive ? [{
    icon: Trophy,
    label: 'Climb the Misl leaderboard with your members'
  }, {
    icon: Sparkles,
    label: 'Unlock group rewards and Court standing together'
  }] : [{
    icon: Share2,
    label: `Invite ${remaining} more ${remaining === 1 ? 'member' : 'members'} to activate your house`
  }, {
    icon: Zap,
    label: 'Every member you bring in can also invite'
  }, {
    icon: Trophy,
    label: 'Reach 5 members to unlock the Misl leaderboard'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="relative border border-[#D4AF37]/40 bg-gradient-to-br from-[#1A1410] via-[#131B2B] to-[#0B0F19] overflow-hidden" data-id="element-1975">
      <CornerOrnaments data-id="element-1976" />
      <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-1977" />
      <div className="absolute -right-20 -top-20 w-56 h-56 bg-[#D4AF37]/10 blur-[100px] rounded-full pointer-events-none" data-id="element-1978" />
      <div className="absolute -left-20 -bottom-20 w-56 h-56 bg-[#8B0000]/10 blur-[100px] rounded-full pointer-events-none" data-id="element-1979" />

      {/* Header strip */}
      <div className="relative z-10 px-5 sm:px-7 py-5 border-b border-[#D4AF37]/20 flex items-start sm:items-center justify-between gap-4 flex-wrap" data-id="element-1980">
        <div className="flex items-center gap-4" data-id="element-1981">
          <motion.div animate={{
          rotate: [0, -3, 3, -3, 0]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          repeatDelay: 3
        }} className="shrink-0 w-12 h-12 border-2 border-[#D4AF37]/60 bg-[#D4AF37]/10 flex items-center justify-center" data-id="element-1982">
            <Crown className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} data-id="element-1983" />
          </motion.div>
          <div className="min-w-0" data-id="element-1984">
            <p className="text-[#D4AF37] text-[9px] tracking-[0.3em] uppercase font-bold mb-1 flex items-center gap-2" data-id="element-1985">
              <Sparkles className="w-3 h-3" data-id="element-1986" />
              Your Founding Misl
            </p>
            <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] leading-tight truncate" data-id="element-1987">
              {misl.name}
            </h3>
            {misl.region && <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.25em] uppercase mt-0.5" data-id="element-1988">
                {misl.region}
              </p>}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0" data-id="element-1989">
          <div className={`flex items-center gap-2 px-3 py-2 border text-[9px] tracking-[0.3em] uppercase font-bold ${isActive ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]' : 'border-[#FDFBF7]/30 bg-[#FDFBF7]/5 text-[#FDFBF7]/80'}`} data-id="element-1990">
            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-[#D4AF37]' : 'bg-[#FDFBF7]/60 animate-pulse'} shadow-[0_0_8px_currentColor]`} data-id="element-1991" />
            {isActive ? 'Active' : 'Forming'}
          </div>
          <button onClick={onManage} className="hidden sm:inline-flex items-center gap-2 px-3.5 py-2 text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/60 text-[10px] tracking-[0.25em] uppercase font-bold transition-colors" data-id="element-1992">
            <Settings className="w-3 h-3" data-id="element-1993" />
            Manage
          </button>
        </div>
      </div>

      {/* Progress section */}
      <div className="relative z-10 px-5 sm:px-7 py-5 border-b border-[#D4AF37]/20" data-id="element-1994">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2" data-id="element-1995">
          <div data-id="element-1996">
            <p className="text-[#FDFBF7]/45 text-[10px] tracking-[0.25em] uppercase mb-1" data-id="element-1997">
              Members
            </p>
            <p className="font-serif text-4xl text-[#FDFBF7] leading-none" data-id="element-1998">
              {memberCount}{' '}
              <span className="text-[#FDFBF7]/30" data-id="element-1999">/ {misl.target}</span>
            </p>
          </div>
          <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-medium" data-id="element-2000">
            {isActive ? '🔥 House Activated' : `${remaining} more to activate`}
          </p>
        </div>
        <div className="h-2 bg-[#D4AF37]/10 overflow-hidden" data-id="element-2001">
          <motion.div animate={{
          width: `${progress}%`
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="h-full bg-gradient-to-r from-[#D4AF37]/60 via-[#D4AF37] to-[#E5C158] shadow-[0_0_15px_rgba(212,175,55,0.4)]" data-id="element-2002" />
        </div>
      </div>

      {/* Member list + What's next */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x divide-[#D4AF37]/15" data-id="element-2003">
        {/* Members */}
        <div className="px-5 sm:px-7 py-5" data-id="element-2004">
          <div className="flex items-center justify-between mb-3" data-id="element-2005">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase" data-id="element-2006">
              Members
            </p>
            <span className="font-mono text-[#D4AF37] text-xs" data-id="element-2007">
              {memberCount} / {misl.target}
            </span>
          </div>
          <div className="space-y-2" data-id="element-2008">
            {misl.members.map(m => <motion.div key={m.id} initial={{
            opacity: 0,
            x: -6
          }} animate={{
            opacity: 1,
            x: 0
          }} className="flex items-center gap-3 px-3 py-2 border border-[#D4AF37]/10 bg-[#0B0F19]/40 hover:border-[#D4AF37]/30 transition-colors" data-id="element-2009">
                <div className={`w-8 h-8 shrink-0 rounded-full border flex items-center justify-center ${m.role === 'founder' ? 'border-[#D4AF37] bg-[#D4AF37]/15' : 'border-[#D4AF37]/30 bg-[#131B2B]'}`} data-id="element-2010">
                  {m.role === 'founder' ? <Crown className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-2011" /> : <User className="w-3.5 h-3.5 text-[#FDFBF7]/50" data-id="element-2012" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-2013">
                  <p className="font-serif text-sm text-[#FDFBF7] leading-tight truncate" data-id="element-2014">
                    {m.name}
                  </p>
                  <p className="font-mono text-[#FDFBF7]/40 text-[10px] truncate" data-id="element-2015">
                    {m.handle} · {m.joinedAt}
                  </p>
                </div>
                {m.role === 'founder' && <span className="shrink-0 text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2 py-0.5" data-id="element-2016">
                    Founder
                  </span>}
                <button onClick={handleWhatsApp} className="shrink-0 text-[#D4AF37]/60 hover:text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 px-2 py-1 transition-colors flex items-center gap-1" data-id="element-2017">
                  <Share2 className="w-2.5 h-2.5" data-id="element-2018" />
                  Invite
                </button>
              </motion.div>)}
            {Array.from({
            length: remaining
          }).map((_, i) => <div key={`empty-${i}`} className="flex items-center gap-3 px-3 py-2 border border-dashed border-[#D4AF37]/15 bg-[#0B0F19]/20" data-id="element-2019">
                <div className="w-8 h-8 shrink-0 rounded-full border border-dashed border-[#D4AF37]/20 bg-[#0B0F19] flex items-center justify-center" data-id="element-2020">
                  <Plus className="w-3 h-3 text-[#D4AF37]/30" data-id="element-2021" />
                </div>
                <p className="text-[#FDFBF7]/30 text-xs italic flex-1" data-id="element-2022">
                  Empty seat
                </p>
                <button onClick={onManage} className="shrink-0 text-[#FDFBF7]/35 hover:text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase transition-colors" data-id="element-2023">
                  Invite
                </button>
              </div>)}
          </div>
        </div>

        {/* What's next */}
        <div className="px-5 sm:px-7 py-5" data-id="element-2024">
          <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-3" data-id="element-2025">
            What&rsquo;s Next
          </p>
          <ul className="space-y-3 mb-5" data-id="element-2026">
            {nextSteps.map((s, i) => <li key={i} className="flex items-start gap-3" data-id="element-2027">
                <div className="w-7 h-7 shrink-0 border border-[#D4AF37]/30 bg-[#131B2B] flex items-center justify-center" data-id="element-2028">
                  <s.icon className="w-3 h-3 text-[#D4AF37]" data-id="element-2029" />
                </div>
                <p className="text-[#FDFBF7]/70 text-xs leading-relaxed pt-1" data-id="element-2030">
                  {s.label}
                </p>
              </li>)}
          </ul>

          {/* Invite block */}
          {!isActive && <div className="border border-[#D4AF37]/25 bg-[#0B0F19]/60 p-3" data-id="element-2031">
              <div className="flex items-center gap-2 border border-[#D4AF37]/20 bg-[#0B0F19] px-2.5 py-2 mb-2.5" data-id="element-2032">
                <span className="font-mono text-[#D4AF37]/80 text-[10px] truncate flex-1" data-id="element-2033">
                  {inviteLink}
                </span>
                <button onClick={handleCopy} className="shrink-0 flex items-center gap-1 px-2 py-0.5 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] tracking-[0.2em] uppercase hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2034">
                  {copied ? <>
                      <Check className="w-2.5 h-2.5" data-id="element-2035" />
                      Copied
                    </> : <>
                      <Copy className="w-2.5 h-2.5" data-id="element-2036" />
                      Copy
                    </>}
                </button>
              </div>
              <button onClick={handleWhatsApp} className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-[#25D366] text-white hover:bg-[#128C7E] transition-colors text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-2037">
                <MessageCircle className="w-3.5 h-3.5" data-id="element-2038" />
                Share on WhatsApp
              </button>
            </div>}

          <button onClick={onManage} className="sm:hidden w-full mt-3 inline-flex items-center justify-center gap-2 px-3.5 py-2.5 text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/60 text-[10px] tracking-[0.25em] uppercase font-bold transition-colors" data-id="element-2039">
            <Settings className="w-3 h-3" data-id="element-2040" />
            Manage Misl
          </button>
        </div>
      </div>
    </motion.div>;
};